import { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router';
import { supabase } from '../../lib/supabase';
import { useSearchParam } from './use-query-params';

export interface Profile {
  id: string;
  username: string;
  email: string;
  display_name: string | null;
  photo_url: string | null;
  blocked: boolean;
}

export function useMe(
  fetchPolicy?: string,
): [Profile | undefined, { loading: boolean }] {
  const [profile, setProfile] = useState<Profile | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMe = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (data) setProfile(data as Profile);
      setLoading(false);
    };

    fetchMe();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchMe();
    });

    return () => subscription.unsubscribe();
  }, []);

  return [profile, { loading }];
}

export function useLazyMe() {
  const [profile, setProfile] = useState<Profile | undefined>();
  const [loading, setLoading] = useState(false);

  const getMe = useCallback(async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single();

    if (data) setProfile(data as Profile);
    setLoading(false);
  }, []);

  return [getMe, profile, { loading }] as const;
}

export function useAuthRedirect({
  authed = true,
  to: toPure = '/auth/profile',
}: {
  authed?: boolean;
  to?: string;
} = {}) {
  const history = useHistory();
  const queries = useSearchParam(['_re']);
  const to = decodeURIComponent(queries?._re || '') || toPure;

  const [me, { loading }] = useMe();

  useEffect(() => {
    if (loading) return;
    if (!authed && !me) history.replace(to);
    if (authed && me) history.replace(to);
  }, [me, loading, to]);
}
